// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useAppSelector } from '@crm-src/lib/store'

// Define the hook function
const usePermissions = () => {
  const permissions = useAppSelector((state) => state.auth.permissions) || []
  const user = useAppSelector((state) => state.auth.user)
  
  // Check if user has a specific permission (backwards compatibility)
  const hasPermission = (resource: string, action: string): boolean => {
    if (!permissions || !Array.isArray(permissions)) return false
    const permissionString = `${resource}:${action}`
    return permissions.includes(permissionString)
  }
  
  // Check if user has page access (new page-based permissions)
  const canAccessPage = (page: string): boolean => {
    // Ensure permissions is an array
    if (!permissions || !Array.isArray(permissions)) return false
    
    // Special case: Admin users should have access to all pages
    // This handles cases where permissions might not be cached yet
    const hasAdminRole = user?.roles && user.roles.some((role: { id: number; name: string; description: string }) =>
      role.name === 'admin' || role.name === 'Admin'
    )
    const hasAdminPermission = permissions.includes('admin:access')
    
    if (hasAdminRole || hasAdminPermission) {
      return true
    }
    
    // Handle both formats: "resellers:access" (full permission string) or "resellers" (page name)
    const permissionToCheck = page.includes(':') ? page : `${page}:access`
    
    return permissions.includes(permissionToCheck)
  }
  
  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissionsList: string[]): boolean => {
    if (!permissions || !Array.isArray(permissions)) return false
    return permissionsList.some(permission => permissions.includes(permission))
  }
  
  // Page-based permission checks
  const canAccessTasks = (): boolean => {
    return canAccessPage('tasks')
  }
  
  const canAccessProspects = (): boolean => {
    return canAccessPage('prospects')
  }
  
  const canAccessCustomers = (): boolean => {
    return canAccessPage('customers')
  }
  
  const canAccessContacts = (): boolean => {
    return canAccessPage('contacts')
  }
  
  const canAccessEmailCampaigns = (): boolean => {
    return canAccessPage('email_campaigns')
  }
  
  const canAccessInvoices = (): boolean => {
    return canAccessPage('invoices')
  }
  
  const canAccessFileManager = (): boolean => {
    return canAccessPage('file_manager')
  }
  
  const canAccessResellers = (): boolean => {
    return canAccessPage('resellers')
  }

  // Reseller WRITE capability — gates the Resellers page's create/edit/delete
  // controls (distinct from canAccessResellers, which only gates VISIBILITY).
  // The dashboard emits the `resellers:manage` string only for an actor holding
  // BOTH crm:resellers AND crm:edit (server/src/auth/crmSessionPermissions.ts:
  // canManageCrmResellers), so a sales_manager manages its OWN resellers while a
  // read-only reseller viewer (granted crm:resellers, no crm:edit) stays
  // view-only. super_admin bypasses via admin:access like every other check.
  // crm-api independently enforces write authority + own-scope on the API.
  const canManageResellers = (): boolean => {
    return isAdmin() || permissions.includes('resellers:manage')
  }
  
  const canAccessFinancialMetrics = (): boolean => {
    return canAccessPage('financial_metrics')
  }

  // The financial wall. `financials:access` is granted only to a holder of the
  // dashboard `crm:financials` permission (or super_admin), and it is the SINGLE
  // gate every monetary surface in the CRM consults — invoice amounts, customer
  // MRR/LTV, prospect deal value, agreement/equipment prices, scheduled-invoice
  // totals, the revenue dashboards. When false, money is redacted EVERYWHERE,
  // not just by hiding the Invoices page. Prefer the <Money> component / the
  // useFinancialAccess hook (components/money) over calling this directly so the
  // redaction stays consistent. `admin:access` (super_admin) bypasses, same as
  // every other page check.
  const canViewFinancials = (): boolean => {
    return canAccessPage('financials')
  }
  
  const canAccessAdmin = (): boolean => {
    return canAccessPage('admin')
  }
  
  const canAccessSuperAdmin = (): boolean => {
    return canAccessPage('super_admin')
  }
  
  // Role-based checks
  const isAdmin = (): boolean => {
    return canAccessAdmin()
  }

  const isSuperAdmin = (): boolean => {
    return canAccessSuperAdmin()
  }

  return {
    hasPermission,
    canAccessPage,
    hasAnyPermission,
    canAccessTasks,
    canAccessProspects,
    canAccessCustomers,
    canAccessContacts,
    canAccessEmailCampaigns,
    canAccessInvoices,
    canAccessFileManager,
    canAccessResellers,
    canManageResellers,
    canAccessFinancialMetrics,
    canViewFinancials,
    canAccessAdmin,
    canAccessSuperAdmin,
    isAdmin,
    isSuperAdmin,
    permissions
  }
}

// Export both named and default for compatibility
export { usePermissions }
export default usePermissions
